import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Briefcase, CheckCircle, ExternalLink } from 'lucide-react';
import JobsTab from '../components/employment/JobsTab';
import EmployersTab from '../components/employment/EmployersTab';
import ResumeTab from '../components/employment/ResumeTab';
import PrepTipsTab from '../components/employment/PrepTipsTab';
import MockInterviewTab from '../components/employment/MockInterviewTab';

export default function EmploymentHubPage() {
  const { user, axiosAuth } = useAuth();
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [resources, setResources] = useState(null);
  const [resume, setResume] = useState({ full_name: '', phone: '', email: '', summary: '', work_history: [], education: [], skills: [] });
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [search, setSearch] = useState('');
  const api = axiosAuth();

  useEffect(() => {
    Promise.all([
      api.get('/jobs').catch(() => ({ data: [] })),
      api.get('/employers').catch(() => ({ data: [] })),
      api.get('/employment/resources').catch(() => ({ data: {} })),
      api.get('/resume').catch(() => ({ data: {} }))
    ]).then(([jobsRes, employersRes, resourcesRes, resumeRes]) => {
      setJobs(jobsRes.data || []);
      setEmployers(employersRes.data || []);
      setResources(resourcesRes.data);
      if (resumeRes.data && Object.keys(resumeRes.data).length > 0) {
        setResume(prev => ({ ...prev, ...resumeRes.data }));
      }
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employment-hub-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Employment Hub</h1>
        <p className="text-muted-foreground text-sm mt-1">Find jobs, build your resume, and prepare for interviews</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 sm:grid-cols-5 w-full max-w-2xl rounded-full p-1 bg-muted/50">
          <TabsTrigger value="jobs" className="rounded-full data-[state=active]:bg-white text-xs" data-testid="tab-jobs">Jobs</TabsTrigger>
          <TabsTrigger value="employers" className="rounded-full data-[state=active]:bg-white text-xs" data-testid="tab-employers">Employers</TabsTrigger>
          <TabsTrigger value="resume" className="rounded-full data-[state=active]:bg-white text-xs" data-testid="tab-resume">Resume</TabsTrigger>
          <TabsTrigger value="prep" className="rounded-full data-[state=active]:bg-white text-xs" data-testid="tab-prep">Prep Tips</TabsTrigger>
          <TabsTrigger value="mock" className="rounded-full data-[state=active]:bg-white text-xs" data-testid="tab-mock">Mock Interview</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-4">
          <JobsTab jobs={jobs} search={search} setSearch={setSearch} setSelectedJob={setSelectedJob} />
        </TabsContent>

        <TabsContent value="employers" className="mt-4">
          <EmployersTab employers={employers} search={search} setSearch={setSearch} />
        </TabsContent>

        <TabsContent value="resume" className="mt-4">
          <ResumeTab api={api} resume={resume} setResume={setResume} />
        </TabsContent>

        <TabsContent value="prep" className="mt-4">
          <PrepTipsTab api={api} resources={resources} />
        </TabsContent>

        <TabsContent value="mock" className="mt-4">
          <MockInterviewTab api={api} />
        </TabsContent>
      </Tabs>

      {/* Job Detail Modal */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="rounded-2xl max-w-lg">
          {selectedJob && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center shrink-0">
                    <Briefcase className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <DialogTitle className="text-left">{selectedJob.title}</DialogTitle>
                    {selectedJob.felony_friendly && (
                      <Badge className="bg-emerald-50 text-emerald-700 mt-1">Second Chance Employer</Badge>
                    )}
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {selectedJob.description && <p className="text-sm text-muted-foreground">{selectedJob.description}</p>}
                <div className="space-y-2 text-sm">
                  {selectedJob.location && <p><strong>Location:</strong> {selectedJob.location}</p>}
                  {selectedJob.wage && <p><strong>Wage:</strong> {selectedJob.wage}</p>}
                  {selectedJob.job_type && <p><strong>Type:</strong> {selectedJob.job_type}</p>}
                  {selectedJob.requirements && <p><strong>Requirements:</strong> {selectedJob.requirements}</p>}
                  {selectedJob.benefits && <p><strong>Benefits:</strong> {selectedJob.benefits}</p>}
                </div>
                {selectedJob.application_link && (
                  <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" onClick={() => window.open(selectedJob.application_link, '_blank')}>
                    <ExternalLink size={14} className="mr-2" /> Apply Now
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
